import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import * as _ from 'lodash-es';

const ACTION = {
  GET_COLUMN_NAME: 'gdl671',
  GET_TIER_DATA: 'fph6uq',
  SAVE_EC_CHANGES: '0jag4h',
  GET_TRANS_TYPE: 'ki6cyg',
  DELETE_CODE_COMBI: '72e1gq',
  GET_SYNC_DROPDOWN: 'wklkxd',
  ENTITY_CHANGE_CLEANUP: '0yx5vx',
};

@Injectable({
  providedIn: 'root',
})
export class EntityUpdateService {
  constructor(private http: HttpClient) {}

  /**
   * Build URL-encoded form body.
   * Objects are JSON.stringified; nulls/undefined are omitted.
   * Uses encodeURIComponent so spaces become %20 (not +).
   */
  private post(
    baseUrl: string,
    actionCode: string,
    params: Record<string, any>,
    withCred = true
  ): Observable<any> {
    const url = `${baseUrl}/saveJsonObject?action_code=${actionCode}`;
    const body = Object.keys(params)
      .filter((key) => params[key] !== undefined && params[key] !== null)
      .map((key) => {
        const val = params[key];
        const strVal = typeof val === 'object' ? JSON.stringify(val) : String(val);
        return encodeURIComponent(key) + '=' + encodeURIComponent(strVal);
      })
      .join('&');
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
    return this.http.post<any>(url, body, {
      headers,
      withCredentials: withCred,
    });
  }

  private get(
    baseUrl: string,
    actionCode: string,
    params: Record<string, any>
  ): Observable<any> {
    const url = `${baseUrl}/loadJsonObject?action_code=${actionCode}`;
    return this.http.get<any>(url, { params, withCredentials: true });
  }

  /**
   * Get tier data - action_code fph6uq.
   * Payload (exact keys): entity_type, entity_type_list, scenario, screen_key, sso_id, tax_year.
   */
  getTierData(
    baseUrl: string,
    globalService: any,
    filterParams: any,
    ssoId: string
  ): Observable<any> {
    const gp = globalService ?? {};
    const fp = filterParams ?? {};
    const entityType = fp.entity_type ?? fp.entity_type_list ?? gp.entity_type ?? gp.entity_type_list ?? '';
    const params: Record<string, string> = {
      entity_type: String(entityType),
      entity_type_list: String(entityType),
      scenario: String(fp.scenario ?? gp.scenario ?? ''),
      screen_key: String(fp.screen_key ?? gp.screen_key ?? ''),
      sso_id: String(ssoId ?? ''),
      tax_year: String(fp.tax_year ?? gp.tax_year ?? ''),
    };
    return this.get(baseUrl, ACTION.GET_TIER_DATA, params);
  }

  /**
   * Get column names - action_code gdl671.
   * Payload (exact keys): entity_type, entity_type_list, scenario, sso_id, tax_year, tier.
   */
  getColumnNames(
    baseUrl: string,
    tier: string,
    globalService: any,
    filterParams: any,
    ssoId: string
  ): Observable<any> {
    const gp = globalService ?? {};
    const fp = filterParams ?? {};
    const entityType = fp.entity_type ?? fp.entity_type_list ?? gp.entity_type ?? gp.entity_type_list ?? '';
    const params: Record<string, string> = {
      entity_type: String(entityType),
      entity_type_list: String(entityType),
      scenario: String(fp.scenario ?? gp.scenario ?? ''),
      sso_id: String(ssoId ?? ''),
      tax_year: String(fp.tax_year ?? gp.tax_year ?? ''),
      tier: String(tier ?? ''),
    };
    return this.get(baseUrl, ACTION.GET_COLUMN_NAME, params);
  }

  /**
   * Get trans type - action_code ki6cyg.
   * Payload (exact keys): entity_type_list, jcd_key, scenario, sso_id, tax_year.
   */
  getTransType(
    baseUrl: string,
    combinationKey: string | number,
    globalService: any,
    filterParams: any,
    ssoId: string
  ): Observable<any> {
    const gp = globalService ?? {};
    const fp = filterParams ?? {};
    const entityTypeList = fp.entity_type_list ?? fp.entity_type ?? gp.entity_type_list ?? gp.entity_type ?? '';
    const params: Record<string, string> = {
      combination_key: String(combinationKey ?? ''),
      entity_type_list: String(entityTypeList),
      jcd_key: String(fp.jcd_key ?? gp.jcd_key ?? '250'),
      scenario: String(fp.scenario ?? gp.scenario ?? ''),
      sso_id: String(ssoId ?? ''),
      tax_year: String(fp.tax_year ?? gp.tax_year ?? ''),
    };
    return this.get(baseUrl, ACTION.GET_TRANS_TYPE, params);
  }

  /**
   * Get sync dropdown data - action_code wklkxd.
   * Payload (exact keys): activity_key, entity_type_list, is_locked, issue_id, issue_id_for_tx_save,
   * issue_short_desc, jcd_key, jcd_ta_key, scenario, screen_key, screen_type, sso_id, tax_year.
   */
  getSyncDropdownData(
    baseUrl: string,
    globalService: any,
    filterParams: any,
    ssoId: string
  ): Observable<any> {
    const gp = globalService ?? {};
    const fp = filterParams ?? {};
    const params: Record<string, string> = {
      activity_key: String(fp.activity_key ?? gp.activity_key ?? ''),
      entity_type_list: String(fp.entity_type_list ?? fp.entity_type ?? gp.entity_type_list ?? gp.entity_type ?? ''),
      is_locked: String(fp.is_locked ?? gp.is_locked ?? 'N'),
      issue_id: String(fp.issue_id ?? gp.issue_id ?? ''),
      issue_id_for_tx_save: String(fp.issue_id_for_tx_save ?? gp.issue_id_for_tx_save ?? ''),
      issue_short_desc: String(fp.issue_short_desc ?? gp.issue_short_desc ?? ''),
      jcd_key: String(fp.jcd_key ?? gp.jcd_key ?? '250'),
      jcd_ta_key: String(fp.jcd_ta_key ?? gp.jcd_ta_key ?? ''),
      scenario: String(fp.scenario ?? gp.scenario ?? ''),
      screen_key: String(fp.screen_key ?? gp.screen_key ?? ''),
      screen_type: String(fp.screen_type ?? gp.screen_type ?? 'grid'),
      sso_id: String(ssoId ?? ''),
      tax_year: String(fp.tax_year ?? gp.tax_year ?? ''),
    };
    return this.get(baseUrl, ACTION.GET_SYNC_DROPDOWN, params);
  }

  saveEntity(
    baseUrl: string,
    clobData: any[],
    tier: string,
    globalService: any,
    filterParams: any,
    ssoId: string,
    _rowData: any
  ): Observable<any> {
    const gp = globalService?.globalParams ?? globalService ?? {};
    const fp = filterParams ?? {};

    // clob_settings = full filterParams copy + jcd_key from globalParams
    const settings: any = { ...fp };
    settings.jcd_key = gp.jcd_key ?? fp.jcd_key ?? '250';
    const jsonSettings = JSON.stringify(settings);

    const jsonObj = JSON.stringify(clobData);

    // Flat params via merge (matching AngularJS exactly)
    const params: any = _.merge({}, gp, fp);
    params.scenarioCode = fp.scenarioCode ?? gp.scenarioCode ?? 'RAF';
    params.is_issue_key_enabled = fp.is_issue_key_enabled ?? gp.is_issue_key_enabled ?? 'N';
    params.clob_data = jsonObj;
    params.clob_settings = jsonSettings;
    params.entity_type = fp.entity_type_list ?? fp.entity_type ?? gp.entity_type_list ?? '';
    params.tier = tier;
    params.process_name = 'SAVE_EC_CHANGES';
    params.sso_id = ssoId;

    return this.post(baseUrl, ACTION.SAVE_EC_CHANGES, params);
  }

  deleteCodeCombi(
    baseUrl: string,
    clobData: any[],
    deleteType: string,
    globalService: any,
    filterParams: any,
    ssoId: string,
    _rowData: any
  ): Observable<any> {
    const gp = globalService?.globalParams ?? globalService ?? {};
    const fp = filterParams ?? {};

    // clob_settings = full filterParams copy + jcd_key from globalParams (matching AngularJS exactly)
    const settings: any = { ...fp };
    settings.jcd_key = gp.jcd_key ?? fp.jcd_key ?? '250';
    const jsonSettings = JSON.stringify(settings);

    const jsonObj = JSON.stringify(clobData);

    const params: any = _.merge({}, gp, fp);
    params.scenarioCode = fp.scenarioCode ?? gp.scenarioCode ?? 'RAF';
    params.is_issue_key_enabled = fp.is_issue_key_enabled ?? gp.is_issue_key_enabled ?? 'N';
    params.clob_data = jsonObj;
    params.clob_settings = jsonSettings;
    params.process_name = 'delete-transaction';
    params.sso_id = ssoId;
    params.delete_type = deleteType;

    return this.post(baseUrl, ACTION.DELETE_CODE_COMBI, params);
  }

  entityChangeCleanup(
    baseUrl: string,
    cngType: string,
    leKey: string,
    globalService: any,
    filterParams: any,
    ssoId: string,
    _rowData: any
  ): Observable<any> {
    const gp = globalService?.globalParams ?? globalService ?? {};
    const fp = filterParams ?? {};

    const params: any = _.merge({}, gp, fp);
    params.process_name = 'entity-change-cleanup';
    params.scenarioCode = fp.scenarioCode ?? gp.scenarioCode ?? 'RAF';
    params.is_issue_key_enabled = fp.is_issue_key_enabled ?? gp.is_issue_key_enabled ?? 'N';
    params.sso_id = ssoId;
    params.cngType = cngType;
    params.leKey = leKey;
    params.oldFg = '';

    return this.post(baseUrl, ACTION.ENTITY_CHANGE_CLEANUP, params);
  }
}
